/**
 * EAA-1822: self-service identity client for the `/profile` screens.
 *
 * Talks to portal-api's `me/password` + `me/sessions` surface, added in
 * portal-api commit 61cacb9 (EAA-1830) — see
 * `services/portal-api/portal_api/directory/me_views.py` and
 * `org_serializers.py::MePasswordChangeSerializer` / `KeycloakSessionSerializer`
 * for the source of truth this file is typed from.
 *
 * NOTE (ownership): this module intentionally lives under `src/pages/profile/`,
 * not `src/api/`, per the EAA-1822 handoff — `src/api/*` is another agent's
 * territory this session must not touch. Same conventions as `src/api/meAccess.ts`
 * / `src/api/orgAdmin.ts` (plain fetch, standard envelope unwrap, typed 401).
 * Migrate this into `src/api/` once that ownership boundary is lifted.
 *
 * Endpoints covered (all identity-only — no `X-Org-Id`/`X-Tenant-Id`, the view
 * always acts on the caller's own `sub`):
 *   POST   /api/v1/me/password       — change the caller's own password.
 *   GET    /api/v1/me/sessions       — list the caller's own Keycloak sessions.
 *   DELETE /api/v1/me/sessions       — end EVERY session for the caller.
 *   DELETE /api/v1/me/sessions/<id>  — end ONE of the caller's own sessions.
 *
 * `fetchMySessions` / `endSession` are now rendered by `ProfilePage.tsx`'s
 * per-device "Phiên đăng nhập" list (EAA-1822) — frame 18
 * (`docs/design/handoff/screens/18-profile.png`) itself has no per-device
 * list, only "Đăng xuất khỏi tất cả" (`endAllSessions`); UX-SPEC.md even
 * lists a session list under "Reviewed and deliberately not added" because
 * portal-api had no such endpoint at design time. EAA-1830 shipped one since,
 * so this session renders it rather than leaving it unused — flagged in the
 * EAA-1822 report as a no-frame addition, not a captured literal.
 */

export interface MePasswordChangeRequest {
    current_password: string;
    new_password: string;
}

/** One Keycloak session row — `KeycloakSessionSerializer`. No token/credential material. */
export interface KeycloakSession {
    id: string;
    ip_address?: string;
    start?: number | null;
    last_access?: number | null;
    clients?: string[];
}

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    request_id?: string;
    data?: T;
}

/** Thrown on a 401 so callers can trigger `login()` instead of showing an error. */
export class ProfileApiUnauthorizedError extends Error {
    constructor() {
        super('Unauthorized');
        this.name = 'ProfileApiUnauthorizedError';
    }
}

/**
 * Surfaces the envelope's translated `description` (i18n'd server-side via
 * `Accept-Language`) plus the numeric `code` so a caller can special-case one
 * outcome (e.g. AUTH_INVALID_CREDENTIALS on a wrong current password) without
 * string-matching the message.
 */
export class ProfileApiError extends Error {
    readonly code: number;
    readonly details?: unknown;
    constructor(message: string, code: number, details?: unknown) {
        super(message);
        this.name = 'ProfileApiError';
        this.code = code;
        this.details = details;
    }
}

async function request<T>(
    apiBase: string,
    token: string,
    path: string,
    init?: { method?: string; body?: unknown }
): Promise<T | undefined> {
    const res = await fetch(`${apiBase}${path}`, {
        method: init?.method ?? 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });

    if (res.status === 401) {
        throw new ProfileApiUnauthorizedError();
    }
    // 204 No Content (both session-delete endpoints) — nothing to parse.
    if (res.status === 204) {
        return undefined;
    }

    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
    if (!res.ok || !envelope?.success) {
        throw new ProfileApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            (envelope as { data?: unknown } | null)?.data
        );
    }
    return envelope.data;
}

/**
 * POST /api/v1/me/password — self-service password change.
 *
 * The body is deliberately narrow (current_password/new_password only — see
 * `MePasswordChangeSerializer`'s docstring: there is no sub/username field on
 * this shape at all). A wrong `current_password` answers a translated
 * AUTH_INVALID_CREDENTIALS description here, not a generic failure — the
 * caller should show it inline under the current-password field.
 */
export async function changePassword(
    apiBase: string,
    token: string,
    body: MePasswordChangeRequest
): Promise<void> {
    await request<{ changed: boolean }>(apiBase, token, '/api/v1/me/password', {
        method: 'POST',
        body,
    });
}

/** GET /api/v1/me/sessions — the caller's own Keycloak sessions. Unused today (see module docstring). */
export async function fetchMySessions(apiBase: string, token: string): Promise<KeycloakSession[]> {
    const data = await request<KeycloakSession[]>(apiBase, token, '/api/v1/me/sessions');
    return data ?? [];
}

/**
 * DELETE /api/v1/me/sessions — "Đăng xuất khỏi tất cả": ends EVERY Keycloak
 * session for the caller, including the one behind this very request. Callers
 * MUST still run the local `logout()` (oidc-client-ts) afterwards to clear the
 * browser's own token/session state and redirect — this call only revokes the
 * server-side Keycloak sessions.
 */
export async function endAllSessions(apiBase: string, token: string): Promise<void> {
    await request<void>(apiBase, token, '/api/v1/me/sessions', { method: 'DELETE' });
}

/** DELETE /api/v1/me/sessions/<id> — end ONE of the caller's own sessions. Unused today (see module docstring). */
export async function endSession(apiBase: string, token: string, sessionId: string): Promise<void> {
    await request<void>(apiBase, token, `/api/v1/me/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
    });
}
